import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { WorkByRecordRequest, WorkByRecordResponse, WorkByHoursClient, WorkByLineRequest, WorkByLineEntryRequest, FileResponse, FileParameter, WorkByLineResponse } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { setToNewZealandTimeZone } from 'src/utility/helper/dateHelper';
import { WorkByModifiedBy, WorkByRecordStatus } from 'src/assets/config/dataConfig';

export interface WorkByHours extends WorkByRecordResponse { 
}

export interface WorkByHoursForm {
    workByRecordId: number;
    vesselWorkByName: string;
    vesselId: number;
    startDate: Date | null;
    endDate: Date | null;
    workByStatusId: number;
    notes: string | null;
    lastModifiedDate: Date | null;
    lastModifiedBy: string | null;
    isDeleted: boolean;
    workByLines: WorkByLineResponse[];
}

export interface WorkByHoursModel extends WorkByRecordRequest {
}

export interface State {
    isLoading: boolean;
    error: boolean;
    workByHoursList: WorkByHours[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    workByHoursList: [],
};

const workByHoursSlice = createSlice({
    name: 'workByHours',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getWorkByHoursListSuccess: (state, action) => {
            state.workByHoursList = action.payload;
            state.isLoading = false;
        },
        createWorkByHoursSuccess:(state, action: PayloadAction<WorkByHours>) => {
            state.workByHoursList = [...state.workByHoursList, action.payload]
            state.isLoading = false;
        },
        updateWorkByHoursSuccess: (state, action: PayloadAction<WorkByHours>) => {
            const updatedItem = action.payload;
            const index = state.workByHoursList.findIndex(item => item.workByRecordId === updatedItem.workByRecordId);
            state.workByHoursList = [...state.workByHoursList.slice(0, index), updatedItem, ...state.workByHoursList.slice(index + 1)];
            state.isLoading = false;
        },
        exportWorkByHoursSuccess: (state, action: PayloadAction<FileResponse>) => {
            state.isLoading = false;
        },
        importWorkByHoursSuccess: (state, action: PayloadAction<WorkByHours>) => {
            const updatedItem = action.payload;
            const index = state.workByHoursList.findIndex(item => item.workByRecordId === updatedItem.workByRecordId);
            state.workByHoursList = [...state.workByHoursList.slice(0, index), updatedItem, ...state.workByHoursList.slice(index + 1)];
            state.isLoading = false;
        }
        //delete
    }
})

// Reducer
export default workByHoursSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getWorkByHoursListSuccess,
} = workByHoursSlice.actions;

// for useSelector
export const selectWorkByHours = (state: RootState) => state.workByHours as State;

// ----------------------------------------------------------------------
export function getAllWorkByHours() {
    return async (dispatch) => {
        dispatch(workByHoursSlice.actions.startLoading());
        try {
            const client = new WorkByHoursClient(undefined, axios);


            const response = await client.getAll(false);

            dispatch(workByHoursSlice.actions.getWorkByHoursListSuccess(response));
            return response;

        } catch (error) {
            dispatch(workByHoursSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createWorkBy(workBy: WorkByHours, showNotification: boolean = true){
    return async (dispatch, getState) => {
        dispatch(workByHoursSlice.actions.startLoading());
        try {
            const request: WorkByRecordRequest = mapResponseToRequest(workBy);
            request.startDate = setToNewZealandTimeZone(request.startDate);
            request.endDate = setToNewZealandTimeZone(request.endDate);
            const client = new WorkByHoursClient(undefined, axios);
            const response = await client.createRecord(request);
            dispatch(workByHoursSlice.actions.createWorkByHoursSuccess(response));
            const workByRecordName = getState().workByHours.workByHoursList.find(record => record.workByRecordId === response.workByRecordId)?.vesselWorkByName || '';

            if (showNotification) {
                toast.success(`Workby record: ${workByRecordName} created successfully!`);
            }
        } catch (error) {
            dispatch(workByHoursSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateWorkBy(workBy: WorkByHours, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(workByHoursSlice.actions.startLoading());
        try {
            const existingRecord = getState().workByHours.workByHoursList.find(
                (r) => r.workByRecordId === workBy.workByRecordId
            );
            const request: WorkByRecordRequest = mapResponseToRequest(workBy);
            request.startDate = setToNewZealandTimeZone(request.startDate);
            request.endDate = setToNewZealandTimeZone(request.endDate);
            const client = new WorkByHoursClient(undefined, axios);
            const response = await client.updateRecord(request);
            dispatch(workByHoursSlice.actions.updateWorkByHoursSuccess(response));

            const workByRecordName = getState().workByHours.workByHoursList.find(record => record.workByRecordId === response.workByRecordId)?.vesselWorkByName || '';
            if (showNotification) {
                const oldStatus = existingRecord?.workByStatusId;
                const updatedStatus = response.workByStatusId;
                const message = (oldStatus !== updatedStatus && updatedStatus !== WorkByRecordStatus.Created)                    
                    ? `Workby record: ${workByRecordName} ${WorkByRecordStatus[updatedStatus].toLowerCase()} successfully! `
                    : `Workby record: ${workByRecordName} updated successfully!`;
                toast.success(message);
            }
        } catch (error) { 
            dispatch(workByHoursSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function exportWorkBy(id: number, showNotification: boolean = true) {
     return async (dispatch): Promise<FileResponse | undefined> => {
        dispatch(workByHoursSlice.actions.startLoading());
        try {
            const client = new WorkByHoursClient(undefined, axios);
            const response = await client.exportPayGlobalCsv(id);
            if (response != null && response.status === 200){
                dispatch(workByHoursSlice.actions.exportWorkByHoursSuccess(response));
                if (showNotification) {
                    toast.success(`Successfully exported WorkBy Record: ${id}`);
                }
                return response;
            }
        } catch (error) {
            dispatch(workByHoursSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function importWorkBy(workByRecordId: number, file: FileParameter, showNotification: boolean = true) {
    return async (dispatch, getState): Promise<any | undefined> => {
        dispatch(workByHoursSlice.actions.startLoading());
        try {
            const client = new WorkByHoursClient(undefined, axios);
            const existingRecord = getState().workByHours.workByHoursList.find(
                (r) => r.workByRecordId === workByRecordId
            );
            const response = await client.importCsv(
                workByRecordId,
                file,
                existingRecord?.vesselWorkByName ?? '',
                existingRecord?.startDate ? new Date(existingRecord.startDate) : new Date(),
                existingRecord?.vesselId ?? 0,
                existingRecord?.notes ?? ''
            );
            if (response != null) {
                dispatch(workByHoursSlice.actions.importWorkByHoursSuccess(response));
                if (showNotification) {
                    toast.success('Successfully imported WorkBy Record');
                }
                return response;
            }
        } catch (error) {
            dispatch(workByHoursSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

function mapResponseToRequest(workBy: WorkByHours) : WorkByRecordRequest{
    const request: WorkByRecordRequest = {
        workByRecordId: workBy.workByRecordId,
        vesselWorkByName: workBy.vesselWorkByName,
        vesselId: workBy.vesselId,
        startDate: workBy.startDate,
        endDate: workBy.endDate,
        workByStatusId: workBy.workByStatusId,
        notes: workBy.notes ?? '',
        modifiedBy: workBy.lastModifiedBy ?? WorkByModifiedBy,
        isDeleted: workBy.isDeleted,
        lines: []
    };
    const reqLines: WorkByLineRequest[] = [];
    workBy.workByLines.forEach((resLine) => {
        const entries: WorkByLineEntryRequest[] = [];
        resLine.workByLineEntries.forEach((resLineEntry) => {
            const entry: WorkByLineEntryRequest = resLineEntry as WorkByLineEntryRequest;
            entries.push(entry);
        });
        const reqLine: WorkByLineRequest = {...resLine, modifiedBy: resLine.lastModifiedBy ?? WorkByModifiedBy, entries: entries
        } as WorkByLineRequest
        reqLines.push(reqLine);
    });
    request.lines = reqLines;

    return request;
}

// delete
